import React, { useCallback, useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Check, X, Trash2, ClipboardCheck, MapPin, Eye, Loader2, Star } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { adminListListings, adminUpdateListingStatus, adminFeatureListing, adminDeleteListing, adminGetSettings, adminPatchSettings } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function AdminModeration() {
  const { toast } = useToast();
  const { refreshPublic, setModerationEnabled: setCtxMod } = useApp();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ pending: [], active: [], rejected: [], sold: [] });
  const [moderation, setModeration] = useState(true);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, a, r, sold] = await Promise.all([
        adminGetSettings(),
        adminListListings("pending"),
        adminListListings("active"),
        adminListListings("rejected"),
        adminListListings("sold"),
      ]);
      setModeration(s.moderation_enabled);
      setCtxMod(s.moderation_enabled);
      setData({ pending: p.items, active: a.items, rejected: r.items, sold: sold.items });
    } finally { setLoading(false); }
  }, [setCtxMod]);

  useEffect(() => { reload(); }, [reload]);

  const toggleMod = async (v) => {
    setModeration(v); setCtxMod(v);
    await adminPatchSettings({ moderation_enabled: v });
    await refreshPublic();
    toast({ title: v ? "Moderare activată" : "Moderare dezactivată", description: v ? "Anunțurile noi sunt verificate manual." : "Anunțurile noi se publică instant." });
  };

  const updateStatus = async (id, status) => { await adminUpdateListingStatus(id, status); toast({ title: status === "active" ? "Aprobat" : status === "rejected" ? "Respins" : "Actualizat" }); reload(); };
  const del = async (id) => { if (!confirm("Șterge definitiv?")) return; await adminDeleteListing(id); toast({ title: "Șters" }); reload(); };
  const feature = async (id, fl) => { await adminFeatureListing(id, fl); toast({ title: fl ? "Marcat promovat" : "Eliminat din promovate" }); reload(); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-white flex items-center gap-2"><ClipboardCheck className="w-6 h-6" style={{ color: "var(--amd-primary)" }} /> Moderare anunțuri</h1>
        <p className="text-sm" style={{ color: "var(--amd-text-muted)" }}>Aprobă sau respinge anunțurile.</p>
      </div>

      <div className="amd-card rounded-lg p-4 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <Label className="text-white font-bold">Verificare manuală anunțuri noi</Label>
          <p className="text-xs mt-1" style={{ color: "var(--amd-text-muted)" }}>Când este activată, anunțurile noi rămân în așteptare până sunt aprobate.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: moderation ? "var(--amd-primary)" : "var(--amd-text-muted)" }}>{moderation ? "ACTIVATĂ" : "DEZACTIVATĂ"}</span>
          <Switch checked={moderation} onCheckedChange={toggleMod} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border" style={{ borderColor: "var(--amd-border)" }}>
          <TabsTrigger value="pending">În așteptare ({data.pending.length})</TabsTrigger>
          <TabsTrigger value="active">Active ({data.active.length})</TabsTrigger>
          <TabsTrigger value="rejected">Respinse ({data.rejected.length})</TabsTrigger>
          <TabsTrigger value="sold">Vândute ({data.sold.length})</TabsTrigger>
        </TabsList>

        {["pending", "active", "rejected", "sold"].map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            {loading ? <div className="py-10 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /></div> :
              data[k].length === 0 ? (
                <div className="amd-card rounded-lg p-10 text-center"><div className="font-bold text-white text-lg">Niciun anunț în această categorie</div></div>
              ) : (
                <div className="grid gap-3">
                  {data[k].map((l) => (
                    <div key={l.id} className="amd-card rounded-lg p-3 flex flex-col sm:flex-row gap-3">
                      <img src={l.images?.[0]} alt={l.title} className="w-full sm:w-32 h-32 sm:h-24 rounded object-cover shrink-0 bg-black" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/anunt/${l.id}`} className="font-bold text-white hover:underline truncate inline-block max-w-full">{l.title}</Link>
                          {l.featured && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-white" style={{ background: "var(--amd-primary)" }}>PROMOVAT</span>}
                        </div>
                        <div className="mt-1 font-black" style={{ color: "var(--amd-primary)" }}>{Number(l.price).toLocaleString("ro-RO")} {l.currency}</div>
                        <div className="mt-1 text-xs flex flex-wrap gap-3" style={{ color: "var(--amd-text-muted)" }}>
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.location || l.city}</span>
                          <span>{l.seller_name} • {l.seller_phone}</span>
                          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {l.views || 0}</span>
                        </div>
                      </div>
                      <div className="flex sm:flex-col gap-2 shrink-0">
                        {k !== "active" && <Button onClick={() => updateStatus(l.id, "active")} className="amd-btn-primary gap-1 h-9 px-3"><Check className="w-4 h-4" /> Aprobă</Button>}
                        {k !== "rejected" && <Button onClick={() => updateStatus(l.id, "rejected")} variant="outline" className="border-white/20 text-white hover:bg-white/5 gap-1 h-9 px-3"><X className="w-4 h-4" /> Respinge</Button>}
                        <Button onClick={() => feature(l.id, !l.featured)} variant="outline" className="border-white/20 text-white hover:bg-white/5 gap-1 h-9 px-3" title="Promovat"><Star className={`w-4 h-4 ${l.featured ? "fill-current" : ""}`} /></Button>
                        <Button onClick={() => del(l.id)} variant="outline" className="border-white/20 text-white hover:bg-white/5 gap-1 h-9 px-3"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
